import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, CreditCard, Ticket, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  SHOW_INFO, SHOW_DATES, CONTACT_INFO, PAYMENT_METHODS, 
  generateWhatsAppUrl 
} from '../data/showData';

const Prenota = () => {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    telefono: '',
    numBiglietti: '1',
    data: '',
    note: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nome.trim() || !formData.cognome.trim() || !formData.telefono.trim()) {
      toast.error('Per favore compila tutti i campi obbligatori');
      return;
    }
    
    // Generate WhatsApp URL and redirect
    const whatsappUrl = generateWhatsAppUrl(formData);
    window.location.href = whatsappUrl;
    
    toast.success('Perfetto! Ti sto reindirizzando su WhatsApp...');
  };

  const totalPrice = parseInt(formData.numBiglietti) * SHOW_INFO.ticketPrice;

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#0a0a0a]">
      <div className="container-custom max-w-4xl">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFD700] transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Prenota i tuoi <span className="text-[#FFD700]">biglietti</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Compila il form e ti contatterò su WhatsApp per confermare la prenotazione
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
              <div className="bg-[#121212] rounded-2xl border border-white/5 p-6 md:p-8 space-y-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Ticket className="w-5 h-5 text-[#FFD700]" />
                  I tuoi dati
                </h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome" className="text-gray-300">Nome *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder="Il tuo nome"
                      className="input-dark"
                      required
                      data-testid="input-nome"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cognome" className="text-gray-300">Cognome *</Label>
                    <Input
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      placeholder="Il tuo cognome"
                      className="input-dark"
                      required
                      data-testid="input-cognome"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-gray-300">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+39 123 456 7890"
                    className="input-dark"
                    required
                    data-testid="input-telefono"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="numBiglietti" className="text-gray-300">Numero biglietti *</Label>
                    <Select 
                      value={formData.numBiglietti} 
                      onValueChange={(value) => handleSelectChange('numBiglietti', value)}
                    >
                      <SelectTrigger className="input-dark" data-testid="select-biglietti">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <SelectItem 
                            key={num} 
                            value={num.toString()}
                            className="text-white hover:bg-white/10"
                          >
                            {num} {num === 1 ? 'biglietto' : 'biglietti'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="data" className="text-gray-300">Data preferita</Label>
                    <Select 
                      value={formData.data} 
                      onValueChange={(value) => handleSelectChange('data', value)}
                    >
                      <SelectTrigger className="input-dark" data-testid="select-data">
                        <SelectValue placeholder="Seleziona una data" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-white/10">
                        {SHOW_DATES.filter(d => d.available).map((show, index) => (
                          <SelectItem 
                            key={index} 
                            value={show.date}
                            className="text-white hover:bg-white/10"
                          >
                            {show.date} - {show.day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note" className="text-gray-300">Note (opzionale)</Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Richieste particolari, accessibilità, ecc."
                    className="input-dark min-h-[100px]"
                    data-testid="input-note"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="btn-whatsapp w-full text-lg py-6 flex items-center justify-center gap-3"
                data-testid="submit-booking-btn"
              >
                <Send className="w-5 h-5" />
                Invia su WhatsApp
              </Button>

              <p className="text-center text-gray-500 text-sm">
                Cliccando "Invia su WhatsApp" si aprirà una chat con il messaggio precompilato
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Summary */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Riepilogo</h3>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-400">
                  <span>Biglietto singolo</span>
                  <span>{SHOW_INFO.ticketPrice}€</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Quantità</span>
                  <span>x{formData.numBiglietti}</span>
                </div>
                <div className="border-t border-white/10 pt-3 flex justify-between text-white font-bold text-xl">
                  <span>Totale</span>
                  <span className="text-[#FFD700]">{totalPrice}€</span>
                </div>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="bg-[#121212] rounded-2xl border border-white/5 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#D946EF]" />
                Metodi di pagamento
              </h3>
              <div className="space-y-4">
                {PAYMENT_METHODS.map((method, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-xl ${method.recommended ? 'bg-[#D946EF]/10 border border-[#D946EF]/30' : 'bg-white/5'}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span>{method.icon}</span>
                      <span className="text-white font-medium">{method.name}</span>
                      {method.recommended && (
                        <span className="text-xs bg-[#D946EF] text-white px-2 py-0.5 rounded-full">
                          Consigliato
                        </span>
                      )}
                    </div>
                    <p className="text-gray-400 text-sm">{method.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-[#FFD700]/10 rounded-2xl border border-[#FFD700]/30 p-6">
              <h3 className="text-lg font-semibold text-[#FFD700] mb-3">Come funziona?</h3>
              <ol className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" />
                  Compili il form e clicchi "Invia"
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" />
                  Ti invio il link per il pagamento
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#25D366] mt-0.5 flex-shrink-0" />
                  Paghi e ricevi conferma con i posti
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prenota;
